import React, { useEffect, useState, useCallback, useMemo } from "react";  
import { generateInvoicePDF } from "../../utils/invoiceUtils";  
import {  
    Card,  
    Col,  
    List,  
    Row,  
    Space,  
    Typography,  
    Button,  
    Skeleton,  
    message,  
    Tag,  
    Popover,  
    Modal,  
} from "antd";  
import { DownloadOutlined } from "@ant-design/icons";  
import { useSettings } from "../../contexts/settingsContext";  
import { searchContestants } from "../../api/contestant";  
import { getInvoicesByContestant } from "../../api/invoice";  
import { getApplicationsByContestant } from "../../api/application";  
import PaymentButton from "../../components/Invoices/invoiceButton";  
import { fetchReceiptsByContestantId } from '../../api/receipt';  
import "./invoices.scss";  
  
// =======================================  
// Main Component  
// =======================================  
const InvoicePage = () => {  
    const { Title, Text, Link } = Typography;  
    const { state: settings } = useSettings();  
  
    // Single source of truth for contestant  
    const [contestant, setContestant] = useState(null);  
    const [loading, setLoading] = useState(true);  
    const [error, setError] = useState(null);  
    const [invoices, setInvoices] = useState([]);  
    const [selectedInvoiceIdx, setSelectedInvoiceIdx] = useState(0);  
    const [applications, setApplications] = useState([]);  
    const [receipts, setReceipts] = useState([]);  
    const [modalVisible, setModalVisible] = useState(false);  
    // Show selected receipt in the modal only  
    const [selectedReceipt, setSelectedReceipt] = useState(null);  
  
    // Fetch everything on mount  
    useEffect(() => {  
  let active = true;  

  const fetchAll = async () => {  
    setLoading(true);  

    try {  
      // Call API
      const contestantsResponse = await searchContestants();  

      // Depending on how searchContestants is written, this may already be response.data
      // but from your log, we know it has the shape { data: [...], total: number }
      const contestants = contestantsResponse.data || contestantsResponse;  

      const user = Array.isArray(contestants) ? contestants[0] : contestants?.[0] || contestants?.data?.[0];

      console.log("contestantsResponse:", contestantsResponse);
      console.log("contestants:", contestants);
      console.log("user:", user);

      if (!user) throw new Error("No contestant found");  

      setContestant(user);  

            // Fetch invoices and applications together; fetch receipts separately
            // because some backends may return an error when no receipts exist.
            const [invoicesData, applicationsData] = await Promise.all([
                getInvoicesByContestant(user._id),
                getApplicationsByContestant(user._id, getIdValue(user)),
            ]);

            let receiptsData = [];
            try {
                receiptsData = await fetchReceiptsByContestantId(user._id);
            } catch (e) {
                console.warn("fetchReceiptsByContestantId failed, defaulting to []", e);
                receiptsData = [];
            }

      console.log(invoicesData);
      console.log(applicationsData);
      console.log(receiptsData);

      const sortedInvoices = (invoicesData || []).sort(  
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)  
      );  

      if (active) {  
        setInvoices(sortedInvoices);  
        setSelectedInvoiceIdx(0);  
        setApplications((applicationsData || []).filter(a => !a.paid));  
        setReceipts(receiptsData || []);  
      }  
    } catch (err) {  
      if (active) setError(err.message || "Error loading data");  
    } finally {  
      if (active) setLoading(false);  
    }  
  };  

  fetchAll();  

  return () => {  
    active = false;  
  };  
}, []);  

  
    function getIdValue(user) {  
        const idVal = user?.id_number;  
        if (idVal && typeof idVal === "string") {  
            const t = idVal.trim().toLowerCase();  
            if (t && t !== "0" && t !== "no") return idVal;  
        }  
        return user?.passport_number || "";  
    }  
  
    // Memoize selected invoice  
    const selectedInvoice = useMemo(() => invoices[selectedInvoiceIdx] || null, [invoices, selectedInvoiceIdx]);  
  
    // Applications filtered only to those on the current invoice  
    const filteredApplications = useMemo(() => {  
        if (!selectedInvoice || !Array.isArray(selectedInvoice.applications)) return [];  
        const ids = new Set(selectedInvoice.applications.map(app => app.application?._id));  
        return applications.filter(app => ids.has(app._id));  
    }, [selectedInvoice, applications]);  
  
    // Title memo  
    const title = useMemo(() => {  
        const name = contestant ? `${contestant.first_name || ""} ${contestant.last_name || ""}` : "";  
        return `Invoice for Contestant: ${name}${selectedInvoice ? ` (Round ${selectedInvoice.roundNumber})` : ""}`;  
    }, [contestant, selectedInvoice]);  
  
    // PDF Handler  
    const handleGeneratePDF = useCallback(() => {  
        if (!selectedInvoice) return message.error("No invoice selected");  
        if (!contestant) return message.error("No contestant data available");  
        generateInvoicePDF({  
            contestantName: `${contestant.first_name} ${contestant.last_name}`,  
            totalEvents: selectedInvoice.applications.length,  
            feeBreakdown: selectedInvoice.fee,  
            applications: filteredApplications,  
        }, settings, filteredApplications);  
    }, [contestant, selectedInvoice, settings, filteredApplications]);  
  
    // Receipts Modal   
    const handleReceiptClick = useCallback((receipt) => {  
        setSelectedReceipt(receipt);  
        Modal.info({  
            title: "Receipt Details",  
            content: (  
                <div>  
                    <p><strong>Amount Paid:</strong> R{receipt?.amount_paid}</p>  
                    <p><strong>Date:</strong> {new Date(receipt?.createdAt).toLocaleDateString()}</p>  
                    <p><strong>Applications:</strong></p>  
                    <ul>  
                        {(receipt?.applications || []).map((app) =>  
                            <li key={app?.application?._id}>{app?.application?.name}</li>  
                        )}  
                    </ul>  
                </div>  
            ),  
        });  
    }, []);  
  
    // =============================================  
    // Render!  
    // =============================================  
    if (loading) {  
        return <Skeleton active paragraph={{ rows: 6 }} />;  
    }  
    if (error) {  
        return <div style={{ color: 'red' }}>{error}</div>;  
    }  
  
    return (  
        <div className="invoice-page">  
            <Row  
                style={{ marginBottom: 15 }}  
                className="dashboard_page_header_area"  
                align="middle"  
                justify="space-between"  
            >  
                <Col>  
                    <div style={{ display: 'flex', flexDirection: 'column' }}>  
                        <Title className="dashboard_page_title" level={4} style={{ margin: 0 }}>  
                            {title}  
                        </Title>  
                        {selectedInvoice?.invoice_number && (  
                            <Text type="secondary" style={{ fontSize: "0.9em" }}>  
                                Invoice Number: {selectedInvoice.invoice_number}  
                            </Text>  
                        )}  
                    </div>  
                </Col>  
                <Col>  
                    <Space>  
                        {selectedInvoice?.paid  
                            ? <>  
                                <Tag color="green">Paid</Tag>  
                                <Button type="primary" onClick={handleGeneratePDF}>  
                                    <DownloadOutlined /> Generate PDF  
                                </Button>  
                                <Button type="primary" onClick={() => setModalVisible(true)}>  
                                    View Receipts  
                                </Button>  
                            </>  
                            : selectedInvoice &&  
                            <>  {settings.nationalApplicationsOpen && (    
                                <PaymentButton invoices={[selectedInvoice]} settings={settings} />
                                )}  
                                <Button type="primary" onClick={handleGeneratePDF}>  
                                    <DownloadOutlined /> Generate PDF  
                                </Button>  
                                <Button type="primary" onClick={() => setModalVisible(true)}>  
                                    View Receipts  
                                </Button>  
                            </>  
                        }  
                    </Space>  
                </Col>  
            </Row>  
  
            <Space direction="vertical" size="middle" style={{ display: "flex" }}>  
                <Row gutter={[16, 16]}>  
                    <Col span={24} lg={16}>  
                        <Space size="middle" style={{ width: "100%" }} direction="vertical">  
                            {selectedInvoice ? (  
                                <Card title="Unpaid Events" bordered={false}>  
                                    <List  
                                        size="small"  
                                        dataSource={filteredApplications}  
                                        renderItem={(item) => (  
                                            <List.Item>  
                                                <List.Item.Meta  
                                                    title={item?.event?.name || "Unknown"}  
                                                    description={`${item?.event?.style || "Unknown Style"  
                                                        } (${item?.event?.solo_bool  
                                                            ? "Solo"  
                                                            : `Group - ${item?.group?.group_name || "Unknown"}`  
                                                        })`}  
                                                />  
                                            </List.Item>  
                                        )}  
                                    />  
                                </Card>  
                            ) : (  
                                <Card><Skeleton active /></Card>  
                            )}  
                        </Space>  
                    </Col>  
                    <Col span={24} lg={8}>  
                        {selectedInvoice && (  
                            <Card title="Invoice Details" bordered={false}>  
                                <List>  
                                    <List.Item>  
                                        <Text>  
                                            Base Fee: R {selectedInvoice.base_fee_paid ? 0 : selectedInvoice.fee.base_fee}  
                                        </Text>  
                                    </List.Item>  
                                    {selectedInvoice.roundNumber === 1 && (  
                                        <List.Item>  
                                            <Text>Group Fee: R {selectedInvoice.fee.group_fee}</Text>  
                                        </List.Item>  
                                    )}  
                                    <List.Item>  
                                        <Text>  
                                            Additional Fee: R {selectedInvoice.fee.additional_fee || 0}{" "}  
                                            <Popover  
                                                title="Additional Fee"  
                                                content="If you have applied for more events than the base fee covers, there is an additional fee per extra event."  
                                            >  
                                                <Link>What is this?</Link>  
                                            </Popover>  
                                        </Text>  
                                    </List.Item>  
                                    <List.Item>  
                                        <Text strong style={{ fontSize: "16px" }}>  
                                            Total: R {selectedInvoice.fee.total_fee_unpaid || 0}  
                                        </Text>  
                                    </List.Item>  
                                </List>  
                                <div style={{ display: "flex", justifyContent: "flex-end" }}>  
                                {settings.nationalApplicationsOpen && (  
                                    <>  
                                      {selectedInvoice.paid ? (  
                                        <Tag color="green">Paid</Tag>  
                                      ) : (  
                                        <PaymentButton invoices={[selectedInvoice]} settings={settings} />  
                                      )}  
                                    </>  
                                  )}  
                                </div>  
                            </Card>  
                        )}  
                    </Col>  
                </Row>  
            </Space>  
            <Modal  
                title="Receipts"  
                open={modalVisible}  
                onCancel={() => setModalVisible(false)}  
                footer={null}  
            >  
                <List  
                    dataSource={receipts}  
                    renderItem={(receipt) => (  
                        <List.Item>  
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>  
                                <span>{`R${receipt.amount_paid} - ${new Date(receipt.createdAt).toLocaleDateString()}`}</span>  
                                <Button  
                                    type="link"  
                                    onClick={() => handleReceiptClick(receipt)}  
                                >View Receipt</Button>  
                            </div>  
                        </List.Item>  
                    )}  
                />  
            </Modal>  
        </div>  
    );  
};  
  
export default InvoicePage;  